import type { NormalStep } from './steps.types';

export const isDone = (s: NormalStep) => s.status === 'completed';

/**
 * Returns true when the step is a hub node (a structural container whose
 * sub-tasks are navigated via the HubOverview checklist page). Hub steps
 * are excluded from the progress percentage calculation and rendered as a
 * single stepper node regardless of how many sub-tasks they own.
 */
export const isHubStep = (s: NormalStep) => s.nodeType === 'hub_step';

/**
 * Returns true when the step is a sub-task that belongs to a hub. Sub-task
 * steps are hidden from the top-level stepper; they are navigated to via
 * the hub checklist page (onOpenSubTask callback).
 */
export const isSubtaskStep = (s: NormalStep) => s.nodeType === 'subtask_step';

/** Whether the Hub attached applicant-visible compliance feedback (e.g. Camp
 *  Contract duration) to this step. Purely a presentation signal: it never
 *  drives access — the step stays editable so the applicant can correct it. */
export const hasComplianceFeedback = (s: NormalStep) =>
  Array.isArray(s.complianceFeedback) && s.complianceFeedback.length > 0;

/** Whether STAFF review requested changes on this step (rejection surface).
 *  Use for rejection-specific analytics; presentation should use
 *  {@link needsAttention}, which also covers Hub compliance feedback. */
export const needsReviewAttention = (s: NormalStep) =>
  s.needsAction === true ||
  ['rejected', 'changes_requested', 'action_required', 'flagged'].includes(s.status);

/** Whether a step carries an applicant-facing review or correction state.
 *  Compliance feedback is treated exactly like a staff rejection: the step
 *  shows the attention badge and is no longer counted as complete. */
export const needsAttention = (s: NormalStep) =>
  needsReviewAttention(s) || hasComplianceFeedback(s);

/**
 * Applicant-facing completion differs from the raw workflow status while a
 * reviewer has requested changes. Keep isDone() as the lifecycle predicate;
 * navigation and progress surfaces use this stricter presentation predicate.
 */
export const isCompleteForApplicant = (s: NormalStep) =>
  isDone(s) && !needsAttention(s);

const hubSubtasks = (hub: NormalStep, steps: NormalStep[]) =>
  (hub.hubSubtaskMap ?? [])
    .map((stepKey) => steps.find((step) => step.stepKey === stepKey))
    .filter((step): step is NormalStep => Boolean(step));

/** A hub must surface attention from a hidden sub-task in top-level navigation. */
export const needsNavigationAttention = (s: NormalStep, steps: NormalStep[]) =>
  needsAttention(s) ||
  (isHubStep(s) && hubSubtasks(s, steps).some(needsAttention));

/** Completion used by top-level navigation, including hub sub-task rollups. */
export const isNavigationComplete = (s: NormalStep, steps: NormalStep[]) => {
  if (!isHubStep(s)) return isCompleteForApplicant(s);
  const expectedSubtaskCount = s.hubSubtaskMap?.length ?? 0;
  const subtasks = hubSubtasks(s, steps);
  return (
    !needsAttention(s) &&
    expectedSubtaskCount > 0 &&
    subtasks.length === expectedSubtaskCount &&
    subtasks.every(isCompleteForApplicant)
  );
};

export function progress(steps: NormalStep[]): { done: number; total: number; pct: number } {
  // Hub steps are structural containers — exclude them from the progress
  // percentage (Asana 1216802150410999). Sub-task steps are the unit of work;
  // they are included as normal. Hub steps themselves have no form content and
  // auto-complete only when all their sub-tasks are complete.
  const countable = steps.filter((s) => !isHubStep(s));
  const done = countable.filter(isCompleteForApplicant).length;
  const total = countable.length || 1;
  return { done, total, pct: Math.round((done / total) * 100) };
}
